import {assert, range, lerp, clamp, boxInit, boxAddPoint, boxIsEmpty, parseHexColor, loadImage, timeNow, fireEvent} from './utils';
import {createVAO, createQuadVAO, GLState} from './webgl';
import {makeStrokePipeline} from './stroke';
import {createStamp} from './stamp';
import {getProgram} from './hot';
import {PenHerder} from './pens';
import {Clock} from './clock';
import {Doodle} from './doodle';
import {Cube, cubeFilenames, cubeFaceNames, cubeFaceNormals, getCubeFace, getCubeUV, getCubeDirection} from './cube';
import penColors from './colors';
import assets from './assets';

import {vec2, vec3, vec4, quat, mat3, mat4} from 'gl-matrix';
import QOI from 'qoijs';

const {abs, floor, ceil, round, min, max, cos, sin, asin, acos, atan, atan2, PI} = Math;

const penColorsRGBA = penColors.map(c => parseHexColor(vec4.create(), c.body.srgb));

// temporaries
const tmpVec2 = vec2.create();
const tmpVec3 = vec3.create();
const tmpVec4 = vec4.create();
const viewMat3 = mat3.create();
const viewQuat = quat.create();

const makeFaceImage = async idx => {
    const url = `envmaps/${cubeFilenames[idx]}.jpg`;
    const image = await loadImage(url);
    const {width: w, height: h} = image;
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, w, h);

    if (0) {
        ctx.save();
        ctx.globalAlpha = 0.3;
        ctx.drawImage(image, 0, 0);
        ctx.restore();
    }

    const gridStep = 64;
    ctx.fillStyle = 'rgba(255 255 255 / .1)';
    for (let y = 0; y < h; y += gridStep) {
        ctx.fillRect(0, y, w, 1);
    }
    ctx.fillRect(0, h-1, w, 1);
    for (let x = 0; x < w; x += gridStep) {
        ctx.fillRect(x, 0, 1, h);
    }
    ctx.fillRect(w-1, 0, 1, h);

    ctx.font = 'bold 64px system-ui';
    ctx.shadowOffsetY = 2;
    ctx.shadowOffsetBlur = 4;
    ctx.shadowColor = 'black';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.save();
    ctx.translate(w/2, h/2);
    //ctx.scale(-1, (idx === 2 || idx === 3) ? -1 : 1);
    ctx.scale(-1, 1);
    const text = cubeFaceNames[idx];
    ctx.fillText(text, 0, 0);
    ctx.restore();
    return canvas;
};

let cubeAtlas;
const makeFaceImage2 = async idx => {
    const atlas = await getCubeAtlas();

    //const url = `envmaps/${cubeFilenames[idx]}.jpg`;
    //const image = await loadImage(url);
    //const {width: w, height: h} = image;

    const size = 1024;
    const w = size, h = size;

    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, w, h);

    if (0) {
        ctx.save();
        ctx.globalAlpha = 0.3;
        ctx.drawImage(image, 0, 0);
        ctx.restore();
    }

    if (1) {
        const [tx, ty] = [
            [2, 1], [0, 1],
            [1, 0], [1, 2],
            [3, 1], [1, 1],
        ][idx];
        ctx.drawImage(atlas,
            size * tx, size * ty, size, size,
            0, 0, size, size);
    }

    const gridStep = 64;
    ctx.fillStyle = 'rgba(255 255 255 / .1)';
    for (let y = 0; y < h; y += gridStep) {
        ctx.fillRect(0, y, w, 1);
    }
    ctx.fillRect(0, h - 1, w, 1);
    for (let x = 0; x < w; x += gridStep) {
        ctx.fillRect(x, 0, 1, h);
    }
    ctx.fillRect(w - 1, 0, 1, h);

    ctx.font = 'bold 64px system-ui';
    ctx.shadowOffsetY = 2;
    ctx.shadowOffsetBlur = 4;
    ctx.shadowColor = 'black';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.save();
    ctx.translate(w / 2, h / 2);
    //ctx.scale(-1, (idx === 2 || idx === 3) ? -1 : 1);
    ctx.scale(-1, 1);
    const text = cubeFaceNames[idx];
    ctx.fillText(text, 0, 0);
    ctx.restore();
    return canvas;
};

function radiusFunc(dist, curve = 3e-3) {
    let x = dist * curve;
    return 1 - (1 / (x + 1));
}

function radiusFunc4(dist, curve=.001) {
    let x = dist * curve;
    return clamp(x ** .5, 0, 1);
}

class PenDynamics {
    id = 0;
    type = 'brush';
    color = vec3.create();
    dir = vec3.create();
    active = false;
    tiltX = 0;
    tiltY = 0;
    rotate = 0;
    pressure = 0;
}

export class SharpieDesignEngine extends EventTarget {

    viewProjInv = mat4.create();
    matBackground = mat4.create();
    texBackground = null;
    texGrain = null;
    cubes = [];
    penDynamics = [];
    visible = false;
    opacity = 0;
    doodle = null;
    doodleRunning = true;
    clock = new Clock;
    elClock = null;
    pointerViewportCoords = vec2.create();

    // saved points for reprocessing the brush stroke
    strokePoints = [];
    previousStrokeInputPoint = null;
    
    constructor({
        renderer,
        camera,
        pipeline,
    }) {
        super();
        this.renderer = renderer;
        this.camera = camera;
        this.pipeline = pipeline;
        this.initGL();
        this.initEvents();
        this.initPaint();
        this.loadGrainTexture();
        this.pens = new PenHerder;
        this.doodle = new Doodle(assets.data.doodle);
    }

    async loadGrainTexture() {
        const {gl, glState} = this;
        const image = await loadImage(assets.images.grain);

        glState.push(
            gl.TEXTURE_BINDING_2D,
            gl.UNPACK_FLIP_Y_WEBGL,
        );
        this.texGrain = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, this.texGrain);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
        gl.generateMipmap(gl.TEXTURE_2D);
        glState.pop();
    }

    // async loadCubeImage(imageUrl) {
    //     if (!imageUrl)
    //         return;
    //
    //     const {gl, glState, cube} = this;
    //     const {texture, size} = cube;
    //
    //     const image = await loadImage(imageUrl);
    //     assert(image.width === 3 * size && image.height === 2 * size);
    //
    //     const updateFace = (face, source) => glState.scoped([
    //         gl.TEXTURE_BINDING_CUBE_MAP,
    //         gl.UNPACK_FLIP_Y_WEBGL,
    //     ], () => {
    //         const target = gl.TEXTURE_CUBE_MAP_POSITIVE_X + face;
    //         gl.bindTexture(gl.TEXTURE_CUBE_MAP, texture);
    //         gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    //         gl.texSubImage2D(target, 0, 0, 0, size, size, gl.RGBA, gl.UNSIGNED_BYTE, source);
    //     });
    //
    //     // XXX need to be careful changing WebGL state with async
    //     for (let face = 0; face < 6; ++face) {
    //         const sx = size * (face >> 1);
    //         const sy = size * (face & 1);
    //         const bitmap = await createImageBitmap(image, sx, sy, size, size);
    //         updateFace(face, bitmap);
    //     }
    // }

    initStamp() {
        const {gl} = this;
        const stampSize = 256;
        this.texStamp = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, this.texStamp);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        const stamp = createStamp(stampSize);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, stampSize, stampSize, 0, gl.RGBA, gl.UNSIGNED_BYTE, stamp);
        gl.generateMipmap(gl.TEXTURE_2D);
    }

    initPrograms() {
        const {gl, glState} = this;

        glState.push(
            gl.CURRENT_PROGRAM,
            gl.VERTEX_ARRAY_BINDING,
        );

        this.programs = {
            cube: getProgram(gl, 'cube'),
            paint: getProgram(gl, 'paint'),
            circle: getProgram(gl, 'circle'),
        };

        // dynamic buffer for dab vertices
        this.drawVerts = new Float32Array(16);
        this.bufDraw = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.bufDraw);
        gl.bufferData(gl.ARRAY_BUFFER, this.drawVerts, gl.DYNAMIC_DRAW);

        // vertex attribute objects
        this.vaos = {
            cube: createQuadVAO(gl, this.programs.cube),
            circle: createQuadVAO(gl, this.programs.circle),
            paint: createVAO(gl, this.programs.paint, {
                aPosition: {
                    buffer: this.bufDraw,
                    size: 2,
                    offset: 0,
                    stride: 16,
                },
                aCoord: {
                    buffer: this.bufDraw,
                    size: 2,
                    offset: 8,
                    stride: 16,
                },
            }),
        };

        glState.pop();
    }

    initCubes() {
        const {gl, glState} = this;

        glState.push(
            gl.VIEWPORT,
            gl.SCISSOR_TEST,
            gl.SCISSOR_BOX,
            gl.FRAMEBUFFER_BINDING);

        this.cubes = {
            doodle: new Cube(gl, 1024),
            user: new Cube(gl, 1024),
        };

        console.log('ok');
        glState.pop();
    }

    initGL() {
        this.glState = new GLState(this.gl);
        this.initPrograms();
        this.initStamp();
        this.initCubes();    // async
    }

    initEvents() {
        const canvas = this.renderer.domElement;
        this.addEventListeners(canvas, 'pointerdown');
    }

    initPaint() {
        this.stroke = null;
        this.strokeBeginTime = 0;
        this.strokeViewport = null;
        this.strokeRayDirection = vec3.create();
        this.dirtyTiles = new Map;
        this.dabQueue = [];
        this.undoStack = [];
        this.undoPtr = 0;
        this.penType = 'brush';
        this.penColor = 0;
        this.penColorRGBA = vec4.create();
        this.penSize = 20;
        this.setPenColor(0);
    }

    strokeBegin(e) {
        const {gl} = this;
        assert(!this.stroke);
        assert(this.dirtyTiles.size === 0);
        this.stroke = makeStrokePipeline(point => this.strokeOutput(point));
        this.strokeBeginTime = e.timeStamp;
        this.strokeViewport = gl.getParameter(gl.VIEWPORT);
        this.strokePoints.length = 0;
        this.previousStrokeInputPoint = null;
        this.strokePoint(e);

        // pause any animation
        this.pauseDoodle();
    }

    updateUserPenDynamics(x=0, y=0) {
        const pd = this.getPenDynamics(0);
        pd.active = this.stroke ? true : false;
        pd.type = this.penType;
        vec3.copy(pd.color, this.penColorRGBA);
        if (pd.active) {
            const viewProjInv = this.getViewProjInv();
            const dir = this.setStrokeRayDirection(viewProjInv, x, y);
            vec3.copy(pd.dir, dir);
            pd.tiltX = .26;
            pd.tiltY = .36;
            pd.rotate = 0;
        }
    }

    getViewportCoords(e) {
        const {gl} = this;
        const {devicePixelRatio: dpr} = window;
        const {clientX, clientY} = e;
        const x = dpr * clientX;

        const yOffset = 0.05;    // 5% height offset
        const y = (1 + yOffset) * gl.drawingBufferHeight - dpr * clientY - 1;

        return vec2.set(this.pointerViewportCoords, x, y);
    }

    strokePoint(e) {
        //console.log('STROKE_POINT');
        assert(this.stroke);

        // [ x y pressure tiltX tiltY time ]
        const [x, y] = this.getViewportCoords(e);
        //const pressure = 1;
        //const tiltX = 0;
        //const tiltY = 0;
        const time = e.timeStamp - this.strokeBeginTime;

        let vx = 0;
        let vy = 0;
        if (this.previousStrokeInputPoint) {
            const dx = x - this.previousStrokeInputPoint.x;
            const dy = y - this.previousStrokeInputPoint.y;
            const dt = time - this.previousStrokeInputPoint.time;
            if (dt > 0) {
                vx = dx / dt;
                vy = dy / dt;
            }
        }

        mat3.fromMat4(viewMat3, this.camera.matrixWorldInverse.elements);
        quat.fromMat3(viewQuat, viewMat3);

        this.stroke([
            //x, y, pressure, tiltX, tiltY, time,
            x, y,
            vx, vy,
            time,
            ...viewQuat,
        ]);

        this.previousStrokeInputPoint = { x, y, time, view: viewQuat };
        this.updateUserPenDynamics(x, y);
    }

    replayStroke() {
        if (this.penType !== 'brush') {
            return;
        }

        this.undo();

        const {strokePoints} = this;
        const totalDistance = strokePoints.at(-1).distance;

        //const taper = 1;    // thin end taper
        const taper = 20;   // fat end taper
        const curve = clamp(taper / totalDistance, .001, 1);
        for (let point of strokePoints) {
            let {x, y, r, distance, view} = point;
            r *= radiusFunc4(totalDistance - distance, curve);
            this.dabQueue.push({ x, y, r, view });
        }
        strokePoints.length = 0;

        this.renderPaint();
        this.saveDirtyTilesToUndoStack();
    }

    strokeEnd(e) {
        assert(this.stroke);
        this.strokePoint(e);
        // XXX is there a flush for stroke pipeline?
        this.stroke = null;

        const isDot = (this.strokePoints.length === 0);
        if (isDot) {
            // paint a dot
            assert(this.previousStrokeInputPoint);
            const {x, y, view} = this.previousStrokeInputPoint;
            let r = {
                brush: 0.5,
                eraser: 0.75,
                bullet: 1.0,
            }[this.penType];
            r *= this.penSize;
            this.dabQueue.push({
                x,
                y,
                r,
                view,
            });
            this.previousStrokeInputPoint = null;
        }


        this.renderPaint();

        this.saveDirtyTilesToUndoStack();
        if (!isDot && this.penType == 'brush') {
            this.replayStroke();
        }
        this.updateUserPenDynamics();

        // resume any animation
        this.resumeDoodle();
    }

    strokeOutput(point) {
        const [
            x,
            y,
            //pressure,
            //tiltX,
            //tiltY,
            vx,
            vy,
            timeDelta,
            q0, q1, q2, q3,
            distance,
            angle,
        ] = point;

        // XXX can we ditch pressure here?

        let _radius;
        const maxRadius = this.penSize;  // viewport units
        if (this.penType === 'brush') {
            //const p = lerp(0.150, 1.00, pressure ** 2);
            //_radius = 35 * p;
            //_radius = radius * p;
            //_radius *= radiusFunc(distance, .001);

            const uu = (1 + clamp(vy/5, -1, 1)) / 2;
            let r = maxRadius * .5;
            r *= lerp(1.5, 0.5, uu);
            r *= radiusFunc4(distance);
            _radius = r;
        } else if (this.penType === 'bullet') {
            //const p = lerp(0.250, 1.00, pressure ** 0.25);
            //_radius = radius * p;
            _radius = maxRadius;
        } else if (this.penType === 'eraser') {
            //const p = 0.75 * lerp(0.250, 1.00, pressure ** 0.25);
            _radius = 0.75 * maxRadius;
        }

        const dab = vec4.fromValues(x, y, _radius, angle);
        const view = quat.fromValues(q0, q1, q2, q3);
        quat.normalize(view, view);

        this.dabQueue.push({
            x,
            y,
            r: _radius,
            view,
        });

        // save for reprocessing
        this.strokePoints.push({
            x,
            y,
            r: _radius,
            distance,
            view,
        });
    }

    addEventListeners(target, ...types) {
        types.forEach(type => target.addEventListener(type, this));
    }

    removeEventListeners(target, ...types) {
        types.forEach(type => target.removeEventListener(type, this));
    }

    handleEvent(e) {
        const {target} = e;

        if (!e.isPrimary)
            return;

        if (e.shiftKey)
            return;

        if (this.penType === 'none')
            return;

        if (e.type === 'pointerdown') {
            e.preventDefault();
            this.strokeBegin(e);
            target.setPointerCapture(e.pointerId);
            this.addEventListeners(target, 'pointermove', 'pointerup');
            return;
        }

        if (e.type === 'pointermove') {
            assert(this.stroke);
            e.preventDefault();

            if (e.getCoalescedEvents) {
                e.getCoalescedEvents().forEach(e => this.strokePoint(e));
            } else {
                this.strokePoint(e);
            }

            return;
        }

        if (e.type === 'pointerup') {
            assert(this.stroke);
            e.preventDefault();
            target.releasePointerCapture(e.pointerId);
            this.strokeEnd(e);
            this.removeEventListeners(target, 'pointerup', 'pointermove');
            return;
        }
    }

    get gl() {
        return this.renderer.getContext();
    }

    render() {
        this.update();
        this.renderPaint();
        this.renderCube();
        this.renderCircle();

        {
            // ugh... better than reset(), I guess
            const {state} = this.renderer;
            state.unbindTexture();
        }

        // since autoClear is false,
        // we should clear the depth for foreground layers
        this.clearDepthBuffer();
        this.renderPens();
    }

    renderPens() {
        this.renderer.render(
            this.pens.scene,
            this.camera);
    }

    clearDepthBuffer() {
        const {gl} = this;
        gl.clear(gl.DEPTH_BUFFER_BIT);
    }

    update() {
        this.clock.update();
        this.updateOpacity();
        this.updatePens();
        this.updateDoodle();

        {
            if (!this.elClock) {
                this.elClock = document.querySelector('.clock');
            }
            if (this.elClock) {
                this.elClock.textContent = (this.clock.time / 1000).toFixed(1);
            }
        }
    }

    updateDoodle() {
        // set all automatic layer dynamics to inactive
        for (let i = 1; i < this.penDynamics.length; ++i) {
            const pd = this.penDynamics[i];
            if (pd)
                pd.active = false;
        }

        if (!this.doodleRunning) {
            return;
        }

        if (!this.doodle) {
            return;
        }

        const dabs = this.doodle.update();
        if (!dabs || dabs.length === 0)
            return;

        this.dabQueue.push(...dabs);

        // update from dabs
        const dir = tmpVec3;
        for (let dab of dabs) {
            const {layer, face, x, y, r, color, brush, tiltX, tiltY, rotate} = dab;
            const pd = this.getPenDynamics(1 + layer);
            pd.type = brush;
            pd.tiltX = tiltX;
            pd.tiltY = tiltY;
            pd.rotate = rotate;
            const faceSize = 1024;
            pd.pressure = clamp(faceSize * r/15, 0, 1);
            //console.log(pd.pressure);
            vec3.copy(pd.color, penColorsRGBA[color]);
            getCubeDirection(pd.dir, face, x, y);
            pd.active = true;
        }
    }

    updateOpacity() {
        const delta = (this.clock.delta * 1e-3) * (this.visible ? 1 : -1);
        this.opacity = clamp(this.opacity + delta, 0, 1);
    }

    renderCube() {
        const {gl, vao, cube, glState, pipeline, matBackground} = this;

        this.glState.push(
            gl.DEPTH_TEST,
            gl.CURRENT_PROGRAM,
            gl.VERTEX_ARRAY_BINDING,
            gl.BLEND,
        );

        const program = this.programs.cube;

        if (pipeline) {
            this.texBackground = pipeline.cameraFrameTextureGL();
            mat4.copy(this.matBackground, pipeline.cameraFrameTextureMatrix(
                gl.drawingBufferWidth,
                gl.drawingBufferHeight,
                false));
        }

        gl.disable(gl.DEPTH_TEST);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

        gl.useProgram(program);

        gl.uniformMatrix4fv(
            gl.getUniformLocation(program, 'uMatViewProjInv'),
            false,
            this.getViewProjInv());

        gl.uniformMatrix4fv(
            gl.getUniformLocation(program, 'uMatBackground'),
            false,
            this.matBackground);

        uniformSamplerCube(gl, program, 'uTexCubeDoodle', 0, this.cubes.doodle.texture);
        uniformSamplerCube(gl, program, 'uTexCubeUser', 1, this.cubes.user.texture);
        uniformSampler2D(gl, program, 'uTexBackground', 2, this.texBackground);
        uniformSampler2D(gl, program, 'uTexGrain', 3, this.texGrain);

        gl.uniform1f(
            gl.getUniformLocation(program, 'uOpacity'),
            this.opacity);

        gl.bindVertexArray(this.vaos.cube);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

        {
            // cleanup
            gl.activeTexture(gl.TEXTURE0);
        }

        this.glState.pop();
    }

    // calculate based on current
    getViewProjInv(viewQuat) {
        const out = this.viewProjInv;
        if (viewQuat) {
            // view direction from argument
            mat4.fromQuat(out, viewQuat);
        } else {
            // view direction from camera
            mat4.copy(out, this.camera.matrixWorldInverse.elements);
        }
        // multiply by camera projection matrix and invert
        mat4.mul(out, this.camera.projectionMatrix.elements, out);
        return mat4.invert(out, out);
    }

    setStrokeRayDirection(viewProjInv, viewportX, viewportY) {
        const P = tmpVec4;
        const {strokeViewport} = this;
        const u = (viewportX - strokeViewport[0]) / strokeViewport[2];
        const v = (viewportY - strokeViewport[1]) / strokeViewport[3];
        vec4.set(P, 2*u - 1, 2*v - 1, 1, 1);
        vec4.transformMat4(P, P, viewProjInv);
        return vec3.normalize(this.strokeRayDirection, P);
    }

    mapDrawVert(face, index, viewProjInv, cx, cy, radius) {
        const s = index & 1;
        const t = (index >> 1) & 1;
        const x = cx + radius * (2*s - 1);
        const y = cy + radius * (2*t - 1);

        const uv = tmpVec2;
        this.setStrokeRayDirection(viewProjInv, x, y);
        getCubeUV(uv, this.strokeRayDirection, face);

        let sp = 4 * index;
        this.drawVerts[sp++] = uv[0];
        this.drawVerts[sp++] = uv[1];
        this.drawVerts[sp++] = s;
        this.drawVerts[sp++] = t;
    }

    mapDrawVert2(index, cx, cy, radius) {
        const s = index & 1;
        const t = (index >> 1) & 1;
        const x = cx + radius * (2*s - 1);
        const y = cy + radius * (2*t - 1);

        let sp = 4 * index;
        this.drawVerts[sp++] = x;
        this.drawVerts[sp++] = y;
        this.drawVerts[sp++] = s;
        this.drawVerts[sp++] = t;
    }

    renderPaint() {
        const {gl} = this;

        if (this.dabQueue.length === 0)
            return;

        this.glState.push(
            gl.VIEWPORT,
            gl.CURRENT_PROGRAM,
            gl.FRAMEBUFFER_BINDING,
            gl.VERTEX_ARRAY_BINDING,
            gl.BLEND,
        );

        const {size} = this.cubes.user;
        gl.viewport(0, 0, size, size);
        gl.enable(gl.BLEND);

        const program = this.programs.paint;
        gl.useProgram(program);

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.texStamp);

        gl.uniform4fv(
            gl.getUniformLocation(program, 'uColor'),
            this.penColorRGBA);

        gl.bindVertexArray(this.vaos.paint);

        const box = boxInit();

        // doodle dabs
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        for (let face = 0; face < 6; ++face) {
            const cube = this.cubes.doodle;
            gl.bindFramebuffer(gl.FRAMEBUFFER, cube.framebuffers[face]);
            for (let dab of this.dabQueue) {
                const {x, y, r, view} = dab;
                if (view) {
                    // ignore user dabs
                    continue;
                }
                if (dab.face !== face)
                    continue;
                boxInit(box);
                for (let i = 0; i < 4; ++i) {
                    this.mapDrawVert2(i, x, y, r);
                    const sp = 4 * i;
                    boxAddPoint(box, this.drawVerts[sp + 0], this.drawVerts[sp + 1]);
                }
                // clamp to unit square
                box[0] = max(0, box[0]);
                box[1] = max(0, box[1]);
                box[2] = min(1, box[2]);
                box[3] = min(1, box[3]);

                if (boxIsEmpty(box)) {
                    continue;
                }

                gl.uniform4fv(
                    gl.getUniformLocation(program, 'uColor'),
                    penColorsRGBA[dab.color]);

                // update the draw vertices
                gl.bindBuffer(gl.ARRAY_BUFFER, this.bufDraw);
                gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.drawVerts);

                // draw a stamp
                gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
            }
        }

        // user tool blending
        if (this.penType === 'eraser') {
            gl.blendFuncSeparate(
                gl.ZERO, gl.ONE_MINUS_SRC_ALPHA,
                gl.ZERO, gl.ONE_MINUS_SRC_ALPHA);
        } else {
            gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        }

        // user dabs
        for (let face = 0; face < 6; ++face) {
            const cube = this.cubes.user;
            gl.bindFramebuffer(gl.FRAMEBUFFER, cube.framebuffers[face]);
            for (let dab of this.dabQueue) {
                const {x, y, r, view} = dab;
                if (!view) {
                    // ignore doodle dabs
                    continue;
                }

                const viewProjInv = this.getViewProjInv(view);
                this.setStrokeRayDirection(viewProjInv, x, y);
                const dot = vec3.dot(this.strokeRayDirection, cubeFaceNormals[face]);
                if (dot < 0.1)
                    continue;

                // map the screenspace footprint to cube face
                boxInit(box);
                for (let i = 0; i < 4; ++i) {
                    this.mapDrawVert(face, i, viewProjInv, x, y, r);
                    const sp = 4 * i;
                    boxAddPoint(box, this.drawVerts[sp + 0], this.drawVerts[sp + 1]);
                }

                // clamp to unit square
                box[0] = max(0, box[0]);
                box[1] = max(0, box[1]);
                box[2] = min(1, box[2]);
                box[3] = min(1, box[3]);

                if (boxIsEmpty(box)) {
                    continue;
                }

                {
                    // box -> tiles
                    const tileSize = 64;
                    const tileDim = cube.size / tileSize;
                    const tx0 = floor(box[0] * tileDim);
                    const ty0 = floor(box[1] * tileDim);
                    const tx1 = ceil(box[2] * tileDim);
                    const ty1 = ceil(box[3] * tileDim);
                    for (let ty = ty0; ty < ty1; ++ty) {
                        for (let tx = tx0; tx < tx1; ++tx) {
                            assert(0 <= tx && tx < tileDim && 0 <= ty && ty < tileDim);
                            const key = `${face}:${ty}:${tx}`;
                            if (!this.dirtyTiles.has(key)) {
                                //console.log('read: %s', key);
                                const x = tileSize * tx;
                                const y = tileSize * ty;
                                const w = tileSize;
                                const h = tileSize;
                                const pixels = new Uint8ClampedArray(4 * h * w);
                                gl.readPixels(x, y, w, h, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
                                this.dirtyTiles.set(key, {
                                    face,
                                    rect: [x, y, w, h],
                                    pixels,
                                });
                            }
                        }
                    }
                    //console.log('tile-box:', face, tx0, ty0, tw, th);
                }

                gl.uniform4fv(
                    gl.getUniformLocation(program, 'uColor'),
                    this.penColorRGBA);

                // update the draw vertices
                gl.bindBuffer(gl.ARRAY_BUFFER, this.bufDraw);
                gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.drawVerts);

                // draw a stamp
                gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
            }
        }
        //console.log(dots.map(d => d.toFixed(2)).join(', '));

        // clear the queue
        this.dabQueue.length = 0;

        // cleanup
        this.glState.pop();
    }

    renderCircle() {
        if (!(this.stroke && this.penType === 'eraser'))
            return;

        const {gl, vao, glState} = this;

        this.glState.push(
            gl.DEPTH_TEST,
            gl.CURRENT_PROGRAM,
            gl.VERTEX_ARRAY_BINDING,
            gl.BLEND,
        );

        const program = this.programs.circle;

        gl.disable(gl.DEPTH_TEST);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

        gl.useProgram(program);

        const {drawingBufferWidth: cw, drawingBufferHeight: ch} = gl;
        const pos = this.pointerViewportCoords;
        const radius = this.penSize * 0.5;
        const circle = tmpVec4;
        vec4.set(circle,
            pos[0] / cw,
            pos[1] / ch,
            radius / cw,
            radius / ch);
        // vec4.set(circle, 0.5, 0.5, 100/cw, 100/ch);

        gl.uniform4fv(
            gl.getUniformLocation(program, 'uCircle'),
            circle);

        gl.bindVertexArray(this.vaos.circle);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

        this.glState.pop();
    }

    saveDirtyTilesToUndoStack() {
        const {gl, glState, dirtyTiles} = this;

        glState.push(
            gl.FRAMEBUFFER_BINDING,
        );

        const cube = this.cubes.user;
        const entry = [];
        for (let {face, rect, pixels} of dirtyTiles.values()) {
            const [x, y, w, h] = rect;

            {
                // read end of stroke and compute xor diff
                gl.bindFramebuffer(gl.FRAMEBUFFER, cube.framebuffers[face]);
                const after = new Uint8ClampedArray(4 * h * w);
                gl.readPixels(x, y, w, h, gl.RGBA, gl.UNSIGNED_BYTE, after);
                for (let i = 0; i < pixels.length; ++i)
                    pixels[i] ^= after[i];
            }

            const data = QOI.encode(pixels, {
                width: w,
                height: h,
                channels: 4,
                colorspace: 0,  // sRGB
            });
            const tile = {
                face,
                data,
                rect,
            };
            entry.push(tile);
        }

        glState.pop();
        dirtyTiles.clear();

        // save to undo stack
        this.undoStack.length = this.undoPtr;
        this.undoStack[this.undoPtr] = entry;
        ++this.undoPtr;

        fireEvent(this, 'history-change');
    }

    applyUndoStackEntryAt(index) {
        const entry = this.undoStack[index];
        assert(entry);

        const {gl, glState} = this;

        glState.push(
            gl.FRAMEBUFFER_BINDING,
            gl.UNPACK_FLIP_Y_WEBGL,
        );

        console.time('applyUndoStackEntryAt');
        const cube = this.cubes.user;
        gl.bindTexture(gl.TEXTURE_CUBE_MAP, cube.texture);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        for (let {face, rect, data} of entry) {
            const image = QOI.decode(data);
            const pixels = new Uint8Array(image.data);

            const [x, y, w, h] = rect;
            assert(image.width == w && image.height === h);

            {
                gl.bindFramebuffer(gl.FRAMEBUFFER, cube.framebuffers[face]);
                const after = new Uint8ClampedArray(4 * h * w);
                gl.readPixels(x, y, w, h, gl.RGBA, gl.UNSIGNED_BYTE, after);
                for (let i = 0; i < pixels.length; ++i)
                    pixels[i] ^= after[i];
            }

            const target = gl.TEXTURE_CUBE_MAP_POSITIVE_X + face;
            gl.texSubImage2D(target, 0, x, y, w, h, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
        }
        console.timeEnd('applyUndoStackEntryAt');

        glState.pop();
        fireEvent(this, 'history-change');
    }

    setPenType(type) {
        assert(['brush', 'bullet', 'eraser', 'none'].includes(type));
        this.penType = type;
    }

    setPenColor(color) {
        this.penColor = color;
        this.setPenColorHex(penColors[color].body.srgb);
    }

    setPenColorHex(hex) {
        parseHexColor(this.penColorRGBA, hex);
    }

    setPenSize(size) {
        this.penSize = size;
    }

    clear() {
        this.clearCubes();
        this.clearUndoStack();
    }

    clearCubes() {
        const {gl, glState, cube} = this;
        glState.push(
            gl.VIEWPORT,
            gl.FRAMEBUFFER_BINDING,
        );
        for (let cube of Object.values(this.cubes)) {
            gl.viewport(0, 0, cube.size, cube.size);
            for (let face = 0; face < 6; ++face) {
                gl.bindFramebuffer(gl.FRAMEBUFFER, cube.framebuffers[face]);
                gl.clearColor(0, 0, 0, 0);
                gl.clear(gl.COLOR_BUFFER_BIT);
            }
        }
        glState.pop();

        this.killDoodle();
    }

    pauseDoodle() {
        if (this.doodle)
            this.doodleRunning = false;
    }

    resumeDoodle() {
        if (!this.doodle)
            return;

        if (this.doodleRunning)
            return;

        this.doodleRunning = true;
        this.doodle.resume();
    }

    killDoodle() {
        this.doodle = null;
        this.doodleRunning = false;
    }

    clearUndoStack() {
        this.undoStack.length = 0;
        this.undoPtr = 0;
        fireEvent(this, 'history-change');
    }

    undo() {
        if (this.canUndo())
            this.applyUndoStackEntryAt(--this.undoPtr);
    }

    redo() {
        if (this.canRedo())
            this.applyUndoStackEntryAt(this.undoPtr++);
    }

    canUndo() {
        return this.undoPtr > 0;
    }

    canRedo() {
        return this.undoPtr < this.undoStack.length;
    }

    setVisible(visible) {
        this.visible = visible;
    }

    get penCount() {
        return this.penDynamics.length;
    }

    getPenDynamics(penIndex) {
        let pd = this.penDynamics[penIndex];
        if (!pd) {
            pd = new PenDynamics;
            this.penDynamics[penIndex] = pd;
        }
        return pd;
    }

    updatePens() {
        this.pens.update(this);

        {
            // user pen
            const pd = this.getPenDynamics(0);
            if (this.stroke) {
                pd.opacity = 1;
            } else {
                const fadeDuration = 300;
                const delta = this.clock.delta / fadeDuration;
                pd.opacity = Math.max(0, pd.opacity - delta);
            }
            vec3.copy(pd.color, this.penColorRGBA);
            pd.type = this.penType;
        }

        {
            // auto pen
        }

        // update the pen herder
        this.pens.update(this);
    }

    pause() {
        this.doodleRunning = false;
    }

    resume() {
        if (this.doodle) {
            this.doodleRunning = true;
            this.doodle.resume();
        }
    }
}

// convenience functions for texture setup
function uniformSampler(gl, program, name, unit, target, texture) {
    const location = gl.getUniformLocation(program, name);
    if (location) {
        gl.activeTexture(gl.TEXTURE0 + unit);
        gl.bindTexture(target, texture);
        gl.uniform1i(location, unit);
    }
}

function uniformSampler2D(gl, program, name, unit, texture) {
    uniformSampler(gl, program, name, unit, gl.TEXTURE_2D, texture);
}

function uniformSamplerCube(gl, program, name, unit, texture) {
    uniformSampler(gl, program, name, unit, gl.TEXTURE_CUBE_MAP, texture);
}
